import React from 'react';
import { Warning, ArrowsClockwise } from '@phosphor-icons/react';
import * as Sentry from '@sentry/react';
import i18n from 'i18next';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-danger-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Warning className="w-8 h-8 text-danger-600" weight="light" />
            </div>
            <h1 className="text-xl font-bold text-navy-800 mb-2">
              {i18n.t('errorBoundary.title')}
            </h1>
            <p className="text-slate-600 mb-6">
              {i18n.t('errorBoundary.body')}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                type="button"
                onClick={this.handleGoHome}
                className="btn-secondary"
              >
                {i18n.t('errorBoundary.goHome')}
              </button>
              <button
                type="button"
                onClick={this.handleReload}
                className="btn-primary flex items-center"
              >
                <ArrowsClockwise className="w-4 h-4 mr-2" weight="light" />
                {i18n.t('errorBoundary.refresh')}
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mt-6 p-4 bg-slate-100 rounded-lg text-left">
                <p className="text-xs font-mono text-danger-600 break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
